import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

const data = [
  { area: "Forehead lines", subareas: ["Frontal"] },
  { area: "Frown lines", subareas: ["Frontal"] },
  { area: "Crow feet", subareas: ["Orbital", "Temporal"] },
  { area: "Under eyes", subareas: ["Orbital"] },
  { area: "Bags", subareas: ["Orbital"] },
  { area: "Dark circles", subareas: ["Orbital", "Nasal"] },
  { area: "Cheeks", subareas: ["Zygomatic", "Buccal"] },
  { area: "Nasolabial folds", subareas: ["Nasal", "Buccal"] },
  { area: "Marionette lines", subareas: ["Lip/Perioral", "Mandibular"] },
  { area: "Lips", subareas: ["Lip/Perioral"] },
  { area: "Chin", subareas: ["Mandibular"] },
  { area: "Jawline", subareas: ["Mandibular"] },
  { area: "Neck", subareas: ["Mandibular"] },
];

const FlowViz = () => {
  const ref = useRef();
  const containerRef = useRef();
  const [selectedAreas, setSelectedAreas] = useState([]);
  const [selectedSubareas, setSelectedSubareas] = useState([]);
  const [containerWidth, setContainerWidth] = useState(1000);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setContainerWidth(entries[0].contentRect.width);
    });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // const width = containerWidth;
    const nodeHeight = 40;
    const verticalPadding = 18;

    const width = containerWidth;
    const isMobile = width < 500;
    // const nodeHeight = isMobile ? 30 : 40;
    const nodeWidth = isMobile ? 0.1 : 0;  // smaller on mobile
    const rectWidth = isMobile ? 10 : 200;

    // console.log('width', width, 'isMobile', isMobile, 'nodeHeight', nodeHeight, 'rectWidth', 
    //   rectWidth, 'verticalPadding', verticalPadding, 'containerWidth', containerWidth, 'nodeWidth', nodeWidth) 

    const subareas = Array.from(new Set(data.flatMap((d) => d.subareas)));

    // Compute total heights of each column
    const totalAreaHeight = data.length * nodeHeight + (data.length - 1) * verticalPadding;
    const totalSubHeight = subareas.length * nodeHeight + (subareas.length - 1) * verticalPadding;

    // SVG height = max column height + some margin
    const svgHeight = Math.max(totalAreaHeight, totalSubHeight) + 50;

    // Step between nodes
    const areaStep = data.length > 1 ? (svgHeight - nodeHeight) / (data.length - 1) : 0;
    const subStep = subareas.length > 1 ? (svgHeight - nodeHeight) / (subareas.length - 1) : 0;

    const svg = d3
      .select(ref.current)
      .attr("viewBox", [0, -20, width, svgHeight+40])
      .style("width", "100%")
      .style("height", "100%")
      // .style("background", "#2D1735");

    svg.selectAll("*").remove();

    const nodesLeft = data.map((d, i) => ({
      id: d.area,
      x: width * (0+nodeWidth),
      y: nodeHeight / 2 + i * areaStep,
      type: "area",
      subareas: d.subareas,
    }));

    const nodesRight = subareas.map((s, i) => ({
      id: s,
      x: width * (1-nodeWidth),
      y: nodeHeight / 2 + i * subStep,
      type: "subarea",
    }));

    const nodes = [...nodesLeft, ...nodesRight];

    const links = data.flatMap((d) =>
      d.subareas.map((s) => ({ source: d.area, target: s }))
    );

    const isLinkActive = (link) => 
      selectedAreas.includes(link.source) || selectedSubareas.includes(link.target);

    // Draw links
    const linkPaths = svg
      .append("g")
      .selectAll("path")
      .data(links)
      .join("path")
      .attr("d", (d) => {
        const source = nodes.find((n) => n.id === d.source);
        const target = nodes.find((n) => n.id === d.target);
        const x1 = source.x + 50;
        const y1 = source.y;
        const x2 = target.x - 50;
        const y2 = target.y;
        const mx = (x1 + x2) / 2;
        return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
      })
      // .attr("stroke", "#8C5523")
      .attr("stroke-width", 2)
      .attr("fill", "none")
      .attr("opacity", 0.8);
  //     .attr("stroke", (l) => (isLinkActive(l) ? "#5F40A1" : "#5F40A1"))
  // .attr("stroke-width", (l) => (isLinkActive(l) ? 4 : 2));

    // Draw nodes
    const nodeGroup = svg
      .append("g")
      .selectAll("g")
      .data(nodes)
      .join("g")
      .style("cursor", "pointer")
      .on("click", (event, d) => {
  if (d.type === "subarea") {
    // Subarea click logic stays the same
    setSelectedSubareas((prev) =>
      prev.includes(d.id)
        ? prev.filter((s) => s !== d.id)
        : [...prev, d.id]
    );
    const linkedAreas = nodesLeft.filter((a) => a.subareas.includes(d.id));
    setSelectedAreas((prev) => {
      const anySelected = linkedAreas.some((a) => prev.includes(a.id));
      if (anySelected) {
        return prev.filter((a) => !linkedAreas.some((la) => la.id === a));
      } else {
        return [...prev, ...linkedAreas.map((a) => a.id)];
      }
    });
  } else {
    // Area click logic
    const areaNode = nodesLeft.find((n) => n.id === d.id);
    const rightSelected = areaNode.subareas.some((s) =>
      selectedSubareas.includes(s)
    );

    setSelectedAreas((prev) => {
      if (prev.includes(d.id)) {
        // Only unselect if none of its subareas are selected
        if (rightSelected) {
          return prev; // Keep it selected
        } else {
          return prev.filter((a) => a !== d.id);
        }
      } else {
        return [...prev, d.id];
      }
    });

    setSelectedSubareas((prev) => {
      const newSubareas = areaNode.subareas;
      const anySelected = newSubareas.some((s) => prev.includes(s));
      if (anySelected) {
        return prev.filter((s) => !newSubareas.includes(s));
      } else {
        return [...prev, ...newSubareas];
      }
    });
  }
});


nodeGroup
  .append("rect")
  .attr("x", (d) => d.x - 100)
  .attr("y", (d) => d.y - nodeHeight / 2)
  .attr("width", 200)
  .attr("height", nodeHeight)
  .attr("rx", 20)
  .attr("fill", (d) =>
    d.type === "area"
      ? selectedAreas.includes(d.id)
        ? "#D7BEBC"
        : "#DF9E60"
      : selectedSubareas.includes(d.id)
      ? "#D7BEBC"
      : "#DF9E60"
  )
  .attr("stroke", (d) =>
    d.type === "area"
      ? selectedAreas.includes(d.id)
        ? "#5F40A1"
        : "#8C5523"
      : selectedSubareas.includes(d.id)
      ? "#5F40A1"
      : "#8C5523"
  )
  .attr("stroke-width", 1);


      nodeGroup
      .append("text")
      .attr("x", (d) => d.x)
      .attr("y", (d) => d.y + 6)
      .attr("text-anchor", "middle")
      .attr("fill", (d) => {
        if (d.type === "area") {
          return selectedAreas.includes(d.id) ? "#5F40A1" : "#8C5523";
        } else {
          return selectedSubareas.includes(d.id) ? "#5F40A1" : "#8C5523";
        }
      })
      .attr("font-size", 18)
      .attr("font-weight", "semibold")
      .text((d) => d.id);
    

      nodeGroup
      .on("mouseover", function (event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr(
            "transform",
            `translate(${d.x},${d.y}) scale(1.03) translate(${-d.x},${-d.y})`
          );
    
        linkPaths
          .transition()
          .duration(200)
          .attr("stroke", (l) =>
            l.source === d.id || l.target === d.id ? "#5F40A1" : (isLinkActive(l) ? "#8C5523" : "#8C5523")
          )
          .attr("stroke-width", (l) =>
            l.source === d.id || l.target === d.id ? 4 : (isLinkActive(l) ? 4 : 2)
          );
      })
      .on("mouseout", function () {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("transform", `translate(0,0)`);
    
        linkPaths.transition().duration(200)
          .attr("stroke", (l) => (isLinkActive(l) ? "#8C5523" : "#8C5523"))
          .attr("stroke-width", (l) => (isLinkActive(l) ? 4 : 2));
      })
    
      .on("mouseout", function () {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("transform", `translate(0,0)`);

        linkPaths.transition().duration(200).attr("stroke", "#8C5523").attr("stroke-width", 2);
      });
  }, [selectedAreas, selectedSubareas, containerWidth]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full absolute rounded-2xl shadow-lg p-4 overflow-x-auto"
      style={{
        width: "100%",
        height: "100%",
        position: "absolute",
        top: "0",
        left: "0",
      }}
    >
      <svg ref={ref}></svg>
    </div>
  );
};

export default FlowViz;
